import type { RefObject } from "react";

/** Tower outline and its three cut-outs. The fill is all four shapes together. */
const TOWER = "M47.9 24.0 L24.50 74.6 Q23.00 77.9 25.70 79.7 L44.30 92.9 Q47.9 95.2 51.50 92.9 L70.10 79.7 Q72.80 77.9 71.30 74.6 Z";
const CUT_TOP = "M47.9 39.9 L53.20 50.6 Q53.60 51.7 52.70 52.4 L48.90 54.6 Q47.9 55.3 46.90 54.6 L43.10 52.4 Q42.20 51.7 42.60 50.6 Z";
const CUT_MID = "M39.90 57.5 L47.9 61.4 L55.90 57.5 Q57.30 57.1 58.20 58.6 L59.00 62.6 Q59.30 63.9 58.10 64.6 L48.90 71.1 Q47.9 71.7 46.90 71.1 L37.70 64.6 Q36.50 63.9 36.80 62.6 L37.60 58.6 Q38.50 57.1 39.90 57.5 Z";
const CUT_LOW = "M35.60 70.6 L47.9 77.6 L60.20 70.6 Q62.70 69.6 63.90 71.9 L64.80 75.4 Q65.10 77.0 63.50 77.9 L49.10 86.2 Q47.9 86.9 46.70 86.2 L32.30 77.9 Q30.70 77.0 31.00 75.4 L31.90 71.9 Q33.10 69.6 35.60 70.6 Z";
const TOWER_FILL = [TOWER, CUT_TOP, CUT_MID, CUT_LOW].join(" ");

/** Signal arcs, each split into two halves that start at the arc's middle and run out to a tip. */
const INNER_ARCS = [
  "M23.10 27.58A19.73 19.73 0 0 0 27.56 40.08",
  "M23.10 27.58A19.73 19.73 0 0 1 27.56 15.08",
  "M71.62 27.55A19.53 19.53 0 0 0 67.20 15.18",
  "M71.62 27.55A19.53 19.53 0 0 1 67.20 39.92",
];
const OUTER_ARCS = [
  "M8.81 27.54A35.04 35.04 0 0 0 17.13 50.20",
  "M8.81 27.54A35.04 35.04 0 0 1 17.13 4.88",
  "M87.15 27.59A34.89 34.89 0 0 0 78.87 5.02",
  "M87.15 27.59A34.89 34.89 0 0 1 78.87 50.16",
];

/**
 * The Integer logo, redrawn as vector from the PNG. Its motion is in tokens.css under
 * `.login-intro-*`; the ids are unique on the page, as the logo is only rendered once.
 */
function IntegerMark() {
  return (
    <svg className="login-intro-logo" width="220" height="220" viewBox="0 0 96 96" role="img" aria-label="Integer logo">
      <defs>
        <clipPath id="login-intro-rise-clip">
          <rect className="login-intro-rise-rect" x="-6" y="0" width="108" height="100" />
        </clipPath>
        <linearGradient id="login-intro-scan-fade" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#9FE3FF" stopOpacity="0" />
          <stop offset="0.5" stopColor="#E8F8FF" stopOpacity="1" />
          <stop offset="1" stopColor="#9FE3FF" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="login-intro-sheen-fade" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset="0.5" stopColor="#fff" stopOpacity="0.75" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <mask id="login-intro-mask" maskUnits="userSpaceOnUse" x="0" y="0" width="96" height="96">
          <path d={TOWER_FILL} fill="#fff" fillRule="evenodd" />
          <circle cx="47.9" cy="26.2" r="6.1" fill="#fff" />
          {[...OUTER_ARCS, ...INNER_ARCS].map((d) => (
            <path key={d} d={d} fill="none" stroke="#fff" strokeWidth="6.2" strokeLinecap="round" />
          ))}
        </mask>
      </defs>

      <path className="login-intro-outline [--d:0.15]" pathLength={1} d={TOWER} />
      <path className="login-intro-outline [--d:0.45]" pathLength={1} d={CUT_TOP} />
      <path className="login-intro-outline [--d:0.62]" pathLength={1} d={CUT_MID} />
      <path className="login-intro-outline [--d:0.79]" pathLength={1} d={CUT_LOW} />
      <path className="login-intro-fill" fillRule="evenodd" d={TOWER_FILL} />
      <rect className="login-intro-scanline" x="20" y="-0.9" width="56" height="1.8" rx="0.9" />
      <circle className="login-intro-ripple [--d:0]" cx="47.9" cy="26.2" r="6.1" />
      <circle className="login-intro-ripple [--d:0.28]" cx="47.9" cy="26.2" r="6.1" />
      <circle className="login-intro-ball" cx="47.9" cy="26.2" r="6.1" />
      <g className="login-intro-wave [--d:2]">
        {INNER_ARCS.map((d) => (
          <path key={d} className="login-intro-signal [--d:2]" pathLength={1} d={d} />
        ))}
      </g>
      <g className="login-intro-wave [--d:2.3]">
        {OUTER_ARCS.map((d) => (
          <path key={d} className="login-intro-signal [--d:2.3]" pathLength={1} d={d} />
        ))}
      </g>
      <g mask="url(#login-intro-mask)">
        <rect className="login-intro-sheen" x="0" y="-6" width="26" height="108" />
      </g>
    </svg>
  );
}

/**
 * Fixed layer for the sign-in intro: the logo flies from the screen centre onto its slot
 * beside the INTEGER label, over a soft glow and a wordmark. It has no background and no
 * pointer events. It is hidden below `lg`, where the slot is not shown.
 */
export function LoginIntro({ markRef }: { markRef: RefObject<HTMLDivElement> }) {
  return (
    <div className="login-intro max-lg:hidden">
      <div className="login-intro-bloom" aria-hidden="true" />
      <div ref={markRef} className="login-intro-mark">
        <IntegerMark />
      </div>
      <div className="login-intro-wordmark" aria-hidden="true">
        <div className="login-intro-letters">
          {"INTEGER".split("").map((letter, i) => (
            <span key={i} className="login-intro-letter">
              {letter}
            </span>
          ))}
        </div>
        <span className="login-intro-rule" />
      </div>
    </div>
  );
}
